import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ArrowRight, Building2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_PARTNER = 'onetreehill';

const ClientPortalModal = ({ isOpen, onClose }) => {
  const [partnerName, setPartnerName] = useState(DEFAULT_PARTNER);
  const [isLoading, setIsLoading] = useState(false);

  const sanitize = (value) => value.toLowerCase().trim().replace(/[^a-z0-9-]/g, '');

  const handleClose = () => {
    setPartnerName(DEFAULT_PARTNER);
    setIsLoading(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const subdomain = sanitize(partnerName);

    if (!subdomain) {
      toast.error('Please enter your partner name');
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await axios.get(`${API}/validate-subdomain/${subdomain}`);
      if (data.valid && data.portal_url) {
        toast.success(`Redirecting to ${subdomain}.willkart.ai...`);
        window.location.href = data.portal_url;
      } else if (data.reason === 'disabled') {
        toast.error('This partner portal is currently disabled. Please contact your partner.');
        setIsLoading(false);
      } else {
        toast.error(`We couldn't find a partner named "${subdomain}". Please check the name with your partner.`);
        setIsLoading(false);
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="client-portal-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Access Your Client Portal
          </DialogTitle>
          <p className="text-slate-400 text-center text-sm mt-2">
            Existing customers log in via their partner&apos;s portal
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5" data-testid="client-portal-form">
          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-medium text-sm">Enter your partner&apos;s name</p>
                <p className="text-slate-400 text-xs mt-1">
                  This is the name of the firm or advisor who set up your will.
                </p>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="partnerName" className="text-slate-300 mb-2 block">
              Partner Name
            </Label>
            <div className="relative flex items-center">
              <Input
                id="partnerName"
                name="partnerName"
                type="text"
                required
                autoFocus
                value={partnerName}
                onChange={(e) => setPartnerName(e.target.value)}
                placeholder="e.g. onetreehill"
                className="pr-32 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20"
                data-testid="client-portal-partner-input"
              />
              <span className="absolute right-3 text-slate-500 text-sm pointer-events-none select-none">
                .willkart.ai
              </span>
            </div>
            <p className="text-slate-500 text-xs mt-2">
              You will be redirected to <span className="text-amber-400">{sanitize(partnerName) || 'your-partner'}.willkart.ai</span>
            </p>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-5 rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-amber-400/30 transition-all duration-300 disabled:opacity-50"
            data-testid="client-portal-submit-btn"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Verifying...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                Go to my Portal
                <ArrowRight className="w-5 h-5" />
              </span>
            )}
          </Button>

          <div className="pt-2 border-t border-slate-800">
            <p className="text-slate-500 text-xs text-center flex items-center justify-center gap-1.5">
              <ExternalLink className="w-3 h-3" />
              Don&apos;t know your partner? Contact the firm or advisor that helped you create your will.
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ClientPortalModal;
